import { useState } from "react";
import { ArrowRight, Check, Phone, X } from "lucide-react";
import CalendlyModal from "@/components/CalendlyModal";
import { Analytics } from "@/lib/analytics";
import { signupUrl } from "@/lib/productApp";
import { PLAN_AMOUNTS, USAGE_PRICE_EUR } from "@/lib/pricing";

const plans = [
  {
    id: "solo" as const,
    name: "Solo",
    note: "Pour les indépendants",
    featured: false,
    features: [
      ["1 utilisateur", true],
      ["1 numéro", true],
      ["Qualification des appels", true],
      ["Dashboard & transcriptions", true],
      ["Prise de rendez-vous & agenda", true],
      ["Alertes SMS", false],
      ["Zapier", false],
      ["Assistant Immovox dans ChatGPT", true],
    ] as const,
  },
  {
    id: "pro" as const,
    name: "Pro",
    note: "Pour les équipes",
    featured: true,
    features: [
      ["Jusqu'à 5 utilisateurs", true],
      ["1 numéro", true],
      ["Qualification des appels", true],
      ["Dashboard & transcriptions", true],
      ["Prise de rendez-vous & agenda", true],
      ["Alertes SMS", true],
      ["Zapier", true],
      ["Assistant Immovox dans ChatGPT", true],
    ] as const,
  },
  {
    id: "business" as const,
    name: "Business",
    note: "Sur mesure",
    featured: false,
    features: [
      ["Utilisateurs illimités", true],
      ["Numéros sur devis", true],
      ["Qualification des appels", true],
      ["Dashboard & transcriptions", true],
      ["Prise de rendez-vous & agenda", true],
      ["Alertes SMS", true],
      ["Zapier", true],
      ["Accompagnement dédié", true],
    ] as const,
  },
];

const Pricing = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="pricing" className="py-16 md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1344px] px-5 md:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Tarifs</p>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-.04em] md:text-[52px] md:leading-[1.04]">
              Des tarifs simples, pensés pour votre agence.
            </h2>
            <p className="mt-5 max-w-[360px] text-[15px] leading-7 text-muted-foreground">
              Choisissez selon la taille de votre équipe et les intégrations utiles. Le volume d'appels ne change pas votre plan.
            </p>
            <div className="mt-7 flex items-center gap-2 text-sm text-foreground/70">
              <Phone className="h-4 w-4 text-primary" />
              {USAGE_PRICE_EUR} de conversation réelle
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="border-t border-foreground/20">
              {plans.map((plan, i) => (
                <div
                  key={plan.id}
                  className={
                    "grid gap-5 border-b border-border py-7 md:grid-cols-[64px_1fr_.75fr_1.45fr_auto] md:items-center " +
                    (plan.featured ? "bg-secondary/45 -mx-4 px-4 md:-mx-6 md:px-6" : "")
                  }
                >
                  <span className="font-mono text-[10px] text-primary">0{i + 1}</span>
                  <div>
                    <h3 className="text-xl font-semibold">{plan.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{plan.note}</p>
                  </div>
                  <div>
                    <span className="text-3xl font-semibold tracking-tight">{PLAN_AMOUNTS[plan.id].EUR}€</span>
                    <span className="text-sm text-muted-foreground"> /mois HT</span>
                  </div>
                  <ul className="space-y-2 text-[13px] text-foreground/75">
                    {plan.features.slice(0, 5).map(([label, included]) => (
                      <li key={label} className={included ? "flex gap-2" : "flex gap-2 text-muted-foreground"}>
                        {included ? <Check className="mt-0.5 h-4 w-4 text-primary" /> : <X className="mt-0.5 h-4 w-4" />}
                        {label}
                      </li>
                    ))}
                  </ul>
                  {plan.id === "business" ? (
                    <button
                      onClick={() => {
                        Analytics.ctaClicked("contact_business", "pricing_section");
                        setOpen(true);
                      }}
                      className="inline-flex h-11 items-center justify-center gap-2 border border-foreground/20 px-4 text-sm font-semibold hover:border-foreground/45"
                    >
                      Nous contacter <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <a
                      href={signupUrl(plan.id)}
                      onClick={() => Analytics.ctaClicked("demarrer_essai_" + plan.id, "pricing_section")}
                      className={
                        plan.featured
                          ? "inline-flex h-11 items-center justify-center gap-2 bg-primary px-4 text-sm font-semibold text-primary-foreground"
                          : "inline-flex h-11 items-center justify-center gap-2 border border-foreground/20 px-4 text-sm font-semibold hover:border-foreground/45"
                      }
                    >
                      Essayer gratuitement <ArrowRight className="h-4 w-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 grid gap-4 text-[13px] text-muted-foreground sm:grid-cols-3">
              <span><b className="block text-foreground">7 jours gratuits</b>Sans carte bancaire</span>
              <span><b className="block text-foreground">Sans engagement</b>Résiliez quand vous voulez</span>
              <span><b className="block text-foreground">Usage identique</b>{USAGE_PRICE_EUR} sur tous les plans</span>
            </div>
          </div>
        </div>
      </div>
      <CalendlyModal open={open} onOpenChange={setOpen} source="pricing_section" />
    </section>
  );
};

export default Pricing;
